import { ref, computed, watch } from 'vue'
import type { Ref } from 'vue'



interface HelpVideoState {
  visible: boolean
  dismissed: boolean
  panelId?: string
  toolUid?: string
  targetElement?: HTMLElement | null
  dismissOnDistance?: boolean
  maxDistanceExceedances?: number
  // Global sequence support
  sequenceNumber?: number
  title?: string
  description?: string
}

interface HelpVideoOptions {
  panelId?: string
  toolUid?: string
  targetElement?: HTMLElement | null
  autoShow?: boolean
  dismissOnDistance?: boolean
  maxDistanceExceedances?: number // How many times distance can be exceeded before dismissal (0 = unlimited)
  showDelay?: number
  // Global sequence support
  sequenceNumber?: number // Position in global sequence (1, 2, 3, etc.)
  title?: string // Custom title for this step
  description?: string // Custom description for this step
}

// Global state for all help video instances
const helpVideoInstances = ref<Map<string, HelpVideoState>>(new Map())
const hoverTimeouts = ref<Map<string, NodeJS.Timeout>>(new Map())

// Global sequence state
const globalSequence = ref<string[]>([]) // Array of instance IDs in sequence order
const currentSequenceIndex = ref(-1) // Current active sequence step (-1 = none active)

export const useHelpVideos = () => {
  const uiStore = useUiStore()
  const { helpVideosEnabled } = storeToRefs(uiStore)

  // Create or get a help video instance
  const createInstance = (id: string, options: HelpVideoOptions = {}) => {
    if (!helpVideoInstances.value.has(id)) {
      helpVideoInstances.value.set(id, {
        visible: false,
        dismissed: false,
        panelId: options.panelId,
        toolUid: options.toolUid,
        targetElement: options.targetElement || null,
        dismissOnDistance: options.dismissOnDistance,
        maxDistanceExceedances: options.maxDistanceExceedances,
        // Global sequence support
        sequenceNumber: options.sequenceNumber,
        title: options.title,
        description: options.description
      })
      
      // Add to global sequence if sequence number is provided
      if (options.sequenceNumber) {
        registerInGlobalSequence(id, options.sequenceNumber)
      }
    }
    return helpVideoInstances.value.get(id)!
  }

  // Get instance state
  const getInstance = (id: string) => {
    return helpVideoInstances.value.get(id)
  }

  // Show help video with optional delay
  const showHelpVideo = (id: string, options: HelpVideoOptions = {}) => {
    if (!helpVideosEnabled.value) return

    const instance = createInstance(id, options)
    
    // Clear any existing timeout for this instance
    const existingTimeout = hoverTimeouts.value.get(id)
    if (existingTimeout) {
      clearTimeout(existingTimeout)
      hoverTimeouts.value.delete(id)
    }

    const showDelay = options.showDelay ?? 500 // Default 500ms delay

    if (showDelay > 0) {
      const timeout = setTimeout(() => {
        if (helpVideosEnabled.value && !instance.dismissed) {
          instance.visible = true
          if (options.panelId) instance.panelId = options.panelId
          if (options.toolUid) instance.toolUid = options.toolUid
          if (options.targetElement) instance.targetElement = options.targetElement
        }
        hoverTimeouts.value.delete(id)
      }, showDelay)
      
      hoverTimeouts.value.set(id, timeout)
    } else {
      if (!instance.dismissed) {
        instance.visible = true
        if (options.panelId) instance.panelId = options.panelId
        if (options.toolUid) instance.toolUid = options.toolUid
        if (options.targetElement) instance.targetElement = options.targetElement
      }
    }
  }

  // Hide help video
  const hideHelpVideo = (id: string) => {
    const instance = getInstance(id)
    if (instance) {
      instance.visible = false
    }

    // Clear any pending timeout
    const timeout = hoverTimeouts.value.get(id)
    if (timeout) {
      clearTimeout(timeout)
      hoverTimeouts.value.delete(id)
    }
  }

  // Dismiss help video (won't show again for this session)
  const dismissHelpVideo = (id: string) => {
    const instance = getInstance(id)
    if (instance) {
      instance.visible = false
      instance.dismissed = true
    }

    // Clear any pending timeout
    const timeout = hoverTimeouts.value.get(id)
    if (timeout) {
      clearTimeout(timeout)
      hoverTimeouts.value.delete(id)
    }
  }

  // Reset dismissed state (allow showing again)
  const resetDismissed = (id: string) => {
    const instance = getInstance(id)
    if (instance) {
      instance.dismissed = false
    }
  }

  // Update target element for sticky positioning
  const updateTargetElement = (id: string, element: HTMLElement | null) => {
    const instance = getInstance(id)
    if (instance) {
      instance.targetElement = element
    }
  }

  // Clear all instances (useful for cleanup)
  const clearAllInstances = () => {
    // Clear all timeouts
    hoverTimeouts.value.forEach(timeout => clearTimeout(timeout))
    hoverTimeouts.value.clear()
    
    // Clear all instances
    helpVideoInstances.value.clear()
  }

  // Global sequence management
  const registerInGlobalSequence = (id: string, sequenceNumber: number) => {
    // Insert ID at the correct position based on sequence number
    const newSequence = [...globalSequence.value]
    
    // Find the correct position (sequence numbers start at 1)
    const insertIndex = sequenceNumber - 1
    
    // Ensure array is large enough
    while (newSequence.length <= insertIndex) {
      newSequence.push('')
    }
    
    newSequence[insertIndex] = id
    globalSequence.value = newSequence.filter(id => id !== '') // Remove empty slots
  }

  const startGlobalSequence = () => {
    if (globalSequence.value.length === 0) return false
    
    // Hide all instances first
    helpVideoInstances.value.forEach(instance => {
      instance.visible = false
    })
    
    // Show first step
    currentSequenceIndex.value = 0
    const firstId = globalSequence.value[0]
    if (firstId) {
      showHelpVideo(firstId)
      return true
    }
    
    return false
  }

  const nextInGlobalSequence = () => {
    if (currentSequenceIndex.value < 0 || currentSequenceIndex.value >= globalSequence.value.length - 1) {
      // End of sequence
      endGlobalSequence()
      return false
    }
    
    // Hide current step
    const currentId = globalSequence.value[currentSequenceIndex.value]
    if (currentId) {
      hideHelpVideo(currentId)
    }
    
    // Show next step
    currentSequenceIndex.value++
    const nextId = globalSequence.value[currentSequenceIndex.value]
    if (nextId) {
      showHelpVideo(nextId)
      return true
    }
    
    return false
  }

  const previousInGlobalSequence = () => {
    if (currentSequenceIndex.value <= 0) return false
    
    // Hide current step
    const currentId = globalSequence.value[currentSequenceIndex.value]
    if (currentId) {
      hideHelpVideo(currentId)
    }
    
    // Show previous step
    currentSequenceIndex.value--
    const prevId = globalSequence.value[currentSequenceIndex.value]
    if (prevId) {
      showHelpVideo(prevId)
      return true
    }
    
    return false
  }

  const skipGlobalSequence = () => {
    // Mark all instances as dismissed
    globalSequence.value.forEach(id => {
      const instance = getInstance(id)
      if (instance) {
        instance.dismissed = true
        instance.visible = false
      }
    })
    
    endGlobalSequence()
  }

  const endGlobalSequence = () => {
    // Hide current step
    if (currentSequenceIndex.value >= 0 && currentSequenceIndex.value < globalSequence.value.length) {
      const currentId = globalSequence.value[currentSequenceIndex.value]
      if (currentId) {
        hideHelpVideo(currentId)
      }
    }
    
    currentSequenceIndex.value = -1
  }

  const getGlobalSequenceProgress = () => {
    if (currentSequenceIndex.value < 0 || globalSequence.value.length === 0) return null
    
    return {
      current: currentSequenceIndex.value + 1,
      total: globalSequence.value.length,
      isLast: currentSequenceIndex.value >= globalSequence.value.length - 1,
      isFirst: currentSequenceIndex.value === 0
    }
  }

  // Check if global sequence is currently active
  const isGlobalSequenceActive = () => {
    return currentSequenceIndex.value >= 0 && globalSequence.value.length > 0
  }

  // Get reactive state for a specific instance
  const getInstanceState = (id: string) => {
    return computed(() => getInstance(id))
  }

  // Helper for tab hover behavior (common pattern)
  const handleTabHover = (id: string, isHovering: boolean, options: HelpVideoOptions = {}) => {
    if (isHovering) {
      showHelpVideo(id, options)
    } else {
      // Don't immediately hide on hover out - let the distance-based opacity handle it
      // Only clear the pending timeout to prevent showing if hover was brief
      const timeout = hoverTimeouts.value.get(id)
      if (timeout) {
        clearTimeout(timeout)
        hoverTimeouts.value.delete(id)
      }
    }
  }

  // Helper for button/element specific help videos
  const handleElementHelp = (
    id: string, 
    targetElement: Ref<HTMLElement | null>, 
    options: Omit<HelpVideoOptions, 'targetElement'> = {}
  ) => {
    const instance = createInstance(id, { ...options, targetElement: targetElement.value })

    // Watch for target element changes
    watch(targetElement, (newElement) => {
      updateTargetElement(id, newElement)
    })

    return {
      show: () => showHelpVideo(id, { ...options, targetElement: targetElement.value }),
      hide: () => hideHelpVideo(id),
      dismiss: () => dismissHelpVideo(id),
      reset: () => resetDismissed(id),
      state: getInstanceState(id)
    }
  }

  // Watch global setting to hide all videos when disabled
  watch(helpVideosEnabled, (enabled) => {
    if (!enabled) {
      helpVideoInstances.value.forEach(instance => {
        instance.visible = false
      })
      // Clear all pending timeouts
      hoverTimeouts.value.forEach(timeout => clearTimeout(timeout))
      hoverTimeouts.value.clear()
      
      // Reset global sequence when help videos are disabled
      endGlobalSequence()
    } else {
      // Reset sequence state when help videos are re-enabled
      // This allows the sequence to start fresh if triggered again
      currentSequenceIndex.value = -1
    }
  })

  return {
    // Instance management
    createInstance,
    getInstance,
    getInstanceState,
    
    // Control methods
    showHelpVideo,
    hideHelpVideo,
    dismissHelpVideo,
    resetDismissed,
    updateTargetElement,
    
    // Global sequence methods
    startGlobalSequence,
    nextInGlobalSequence,
    previousInGlobalSequence,
    skipGlobalSequence,
    endGlobalSequence,
    getGlobalSequenceProgress,
    isGlobalSequenceActive,
    
    // Utility methods
    clearAllInstances,
    handleTabHover,
    handleElementHelp,
    
    // Global state
    helpVideosEnabled,
    instances: computed(() => helpVideoInstances.value)
  }
} 